// @ts-check
import { ArrowLeft, ExternalLink, Mail } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import atelierAdminService, { PROSPECT_LABELS, PROSPECT_STATUSES, STAGE_LABELS } from '../services/atelierAdminService';

export default function AtelierProspectDetailPage() {
  const { prospectId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await atelierAdminService.getProspect(prospectId));
    } catch (e) {
      toast.error(e.message || 'Failed to load prospect');
    }
  }, [prospectId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) return <div className="p-6 text-muted-foreground/70">Loading…</div>;
  const { prospect, account, projects, outreach } = data;

  const setStatus = async (status) => {
    try {
      await atelierAdminService.updateProspect(prospect.id, { status });
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };
  const convert = async () => {
    try {
      await atelierAdminService.convertProspect(prospect.id);
      toast.success('Marked converted');
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div>
      <button
        onClick={() => navigate('/atelier/crm')}
        className="flex items-center gap-1 text-sm text-muted-foreground mb-3 hover:text-foreground"
      >
        <ArrowLeft size={16} /> Back to CRM
      </button>
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-semibold">{prospect.company_name}</h1>
          <p className="text-sm text-muted-foreground">
            {prospect.contact_name || '—'} · {prospect.email} · {prospect.country || '—'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={prospect.status}
            onChange={(e) => setStatus(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            {PROSPECT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {PROSPECT_LABELS[s]}
              </option>
            ))}
          </select>
          {prospect.status !== 'converted' && (
            <button onClick={convert} className="px-3 py-2 rounded-lg bg-green-600 text-white text-sm">
              Mark converted
            </button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-6">
          <Compose prospect={prospect} onLogged={load} />

          <section className="bg-card border rounded-lg p-4">
            <h3 className="font-medium mb-3">Outreach history</h3>
            <ul className="space-y-2 text-sm">
              {outreach.map((o) => (
                <li key={o.id} className="border-b pb-2">
                  <div className="flex justify-between">
                    <span className="font-medium">{o.template_name || o.subject || 'Outreach'}</span>
                    <span className="text-muted-foreground/70 text-xs">{new Date(o.sent_at).toLocaleDateString()}</span>
                  </div>
                  {o.subject && <div className="text-muted-foreground text-xs">{o.subject}</div>}
                </li>
              ))}
              {outreach.length === 0 && <li className="text-muted-foreground/70">No outreach yet.</li>}
            </ul>
          </section>
        </div>

        <div className="space-y-6">
          <section className="bg-card border rounded-lg p-4">
            <h3 className="font-medium mb-2">Journey</h3>
            {account ? (
              <div className="text-sm">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300">
                    Customer
                  </span>
                  <span className="font-medium">{account.company_name}</span>
                  <span className="text-muted-foreground/70">({account.status})</span>
                </div>
                <div className="text-muted-foreground mb-3">{account.business_email}</div>
                <div className="text-xs font-medium text-muted-foreground mb-1">Orders</div>
                <ul className="space-y-1">
                  {projects.map((p) => (
                    <li key={p.id}>
                      <button
                        onClick={() => navigate(`/atelier/projects/${p.id}`)}
                        className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                      >
                        <ExternalLink size={12} /> {p.reference_code} — {p.title}{' '}
                        <span className="text-muted-foreground/70">({STAGE_LABELS[p.stage] || p.stage})</span>
                      </button>
                    </li>
                  ))}
                  {projects.length === 0 && <li className="text-muted-foreground/70 text-sm">No orders yet.</li>}
                </ul>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground/70">
                Not yet a customer. When they register a trade account with {prospect.email}, converting will link their
                journey here.
              </p>
            )}
          </section>

          <section className="bg-card border rounded-lg p-4">
            <h3 className="font-medium mb-2">Notes</h3>
            <NotesEditor prospect={prospect} onSaved={load} />
          </section>
        </div>
      </div>
    </div>
  );
}

function Compose({ prospect, onLogged }) {
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [templateName, setTemplateName] = useState('');

  useEffect(() => {
    atelierAdminService
      .listTemplates('outreach')
      .then(setTemplates)
      .catch(() => {});
  }, []);

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    setTemplateId(id);
    setTemplateName(t?.name || '');
    const fill = (s) =>
      (s || '')
        .replace(/\{\{brand_name\}\}/g, prospect.company_name)
        .replace(/\{\{contact_name\}\}/g, prospect.contact_name || 'there');
    setSubject(fill(t?.subject));
    setBody(fill(t?.body));
  };

  const logSent = async () => {
    try {
      await atelierAdminService.logOutreach(prospect.id, {
        template_id: templateId || null,
        template_name: templateName || null,
        subject,
        body,
      });
      toast.success('Logged as sent');
      onLogged();
    } catch (e) {
      toast.error(e.message || 'Failed');
    }
  };

  const copy = () => {
    navigator.clipboard?.writeText(`Subject: ${subject}\n\n${body}`);
    toast.success('Copied');
  };

  return (
    <section className="bg-card border rounded-lg p-4">
      <h3 className="font-medium mb-3 flex items-center gap-2">
        <Mail size={16} /> Compose outreach
      </h3>
      <select
        value={templateId}
        onChange={(e) => applyTemplate(e.target.value)}
        className="w-full border rounded-lg px-3 py-2 text-sm mb-2"
      >
        <option value="">Pick a template…</option>
        {templates.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
      <input
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        placeholder="Subject"
        className="w-full border rounded-lg px-3 py-2 text-sm mb-2"
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={7}
        placeholder="Body"
        className="w-full border rounded-lg px-3 py-2 text-sm mb-2"
      />
      <div className="flex gap-2">
        <button onClick={copy} className="px-3 py-2 rounded-lg bg-muted text-sm">
          Copy
        </button>
        <button
          onClick={logSent}
          disabled={!subject && !body}
          className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm disabled:opacity-40"
        >
          Log as sent
        </button>
      </div>
      <p className="text-xs text-muted-foreground/70 mt-2">
        Send from kaay.co.in and log it here to track conversion. (Auto-send coming later.)
      </p>
    </section>
  );
}

function NotesEditor({ prospect, onSaved }) {
  const toast = useToast();
  const [notes, setNotes] = useState(prospect.notes || '');
  const save = async () => {
    try {
      await atelierAdminService.updateProspect(prospect.id, { notes });
      toast.success('Saved');
      onSaved();
    } catch (e) {
      toast.error(e.message);
    }
  };
  return (
    <div>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={4}
        className="w-full border rounded-lg px-3 py-2 text-sm mb-2"
      />
      <button onClick={save} className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm">
        Save notes
      </button>
    </div>
  );
}
